import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import buildJobsEmptyFilterChips, {
  chipsAreOnlyPageIdentity,
} from './buildJobsEmptyFilterChips';
import styles from './JobsEmptyState.module.scss';

const CLEAR_ALL_FOCUS_KEY = 'clear-all';

/**
 * Empty results card for Newest-first /jobs when the first batch loaded empty.
 */
const JobsEmptyState = ({
  filter,
  suggestedRoles,
  pageType,
  pageRole,
  pageCountry,
  pageCity,
  focusChipKey,
  onFocusChipKeyConsumed,
  onRemoveChip,
  onFiltersReset,
}) => {
  const chipButtonRefs = useRef({});
  const clearAllRef = useRef(null);
  const headingRef = useRef(null);

  const allChips = buildJobsEmptyFilterChips(filter, { suggestedRoles });
  const showChips = !chipsAreOnlyPageIdentity(allChips, {
    pageRole,
    pageCountry,
    pageCity,
  });
  const chips = showChips ? allChips : [];

  useEffect(() => {
    if (!focusChipKey) return undefined;

    const focusTarget =
      focusChipKey === CLEAR_ALL_FOCUS_KEY
        ? clearAllRef.current
        : chipButtonRefs.current[focusChipKey];

    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    } else if (clearAllRef.current) {
      clearAllRef.current.focus();
    } else if (headingRef.current) {
      headingRef.current.focus();
    }

    if (onFocusChipKeyConsumed) {
      onFocusChipKeyConsumed();
    }
    return undefined;
  }, [focusChipKey, chips, onFocusChipKeyConsumed]);

  const handleRemove = (chip, index) => {
    const nextChip = chips[index + 1];
    const nextFocusKey = nextChip ? nextChip.key : CLEAR_ALL_FOCUS_KEY;
    if (onRemoveChip) {
      onRemoveChip(chip, nextFocusKey);
    }
  };

  const heading = showChips
    ? 'No jobs match these filters'
    : 'No jobs here yet';

  const subline = showChips ? (
    'Remove a filter to see more. Your other filters stay as they are.'
  ) : pageType === 'jobs' ? (
    <>
      Try another role or country, or{' '}
      <a href="/jobs/categories">see every role</a>.
    </>
  ) : (
    <>
      Try another role or country, or <a href="/jobs">see all jobs</a>.
    </>
  );

  return (
    <div
      className={styles.card}
      role="status"
      data-testid="jobs-empty-state"
      data-variant={showChips ? 'filtered' : 'no-filter'}
    >
      <h2
        className={styles.heading}
        data-testid="jobs-empty-heading"
        tabIndex={-1}
        ref={headingRef}
      >
        {heading}
      </h2>
      <p className={styles.subline} data-testid="jobs-empty-subline">
        {subline}
      </p>
      {showChips && (
        <ul className={styles.chipRow} data-testid="jobs-empty-chips">
          {chips.map((chip, index) => (
            <li key={chip.key} className={styles.chip}>
              <span className={styles.chipLabel}>{chip.label}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${chip.label}`}
                data-testid={`jobs-empty-chip-remove-${chip.key}`}
                ref={(el) => {
                  chipButtonRefs.current[chip.key] = el;
                }}
                onClick={() => handleRemove(chip, index)}
              >
                <X size={14} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className={styles.clearAll}
              data-testid="jobs-empty-clear-all"
              ref={clearAllRef}
              onClick={onFiltersReset}
            >
              Clear all
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

JobsEmptyState.propTypes = {
  filter: PropTypes.object.isRequired,
  suggestedRoles: PropTypes.array,
  pageType: PropTypes.oneOf(['jobs', 'role', 'country']).isRequired,
  pageRole: PropTypes.string,
  pageCountry: PropTypes.string,
  pageCity: PropTypes.string,
  focusChipKey: PropTypes.string,
  onFocusChipKeyConsumed: PropTypes.func,
  onRemoveChip: PropTypes.func.isRequired,
  onFiltersReset: PropTypes.func.isRequired,
};

JobsEmptyState.defaultProps = {
  suggestedRoles: [],
  pageRole: null,
  pageCountry: null,
  pageCity: null,
  focusChipKey: null,
  onFocusChipKeyConsumed: undefined,
};

export default JobsEmptyState;
export { CLEAR_ALL_FOCUS_KEY };
